import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { numify } from 'numify';
/////////////////////////////////////////////////////////////
import { DailyComboCard } from './components';
import { useCustomSelector } from '@/core/redux/hooks';
import { ComboCard } from '@/core/interfaces';
import {
	Button,
	ClaimDailyCombo,
	CoinImage,
	RevealDailyCombo,
	DiamondImage,
	InfoDailyCombo,
} from '@/app/shared/components';

interface DailyComboProps {
	onCloseClaim: () => void;
}

export const DailyCombo: React.FC<DailyComboProps> = ({
	onCloseClaim,
}: DailyComboProps) => {
	/*********************
	// #region Hooks
	**********************/
	const { t } = useTranslation();

	/*********************
	// #region Redux
	**********************/
	const { comboCards, canClaimReward, rewardCollected } = useCustomSelector(
		(state) => state.categories
	);
	const { levelCombo } = useCustomSelector((state) => state.counter);

	/*********************
	// #region States
	**********************/
	const [visibleDrawerClaimCombo, setVisibleDrawerClaimCombo] =
		useState<boolean>(false);
	const [visibleRevealDailyCombo, setVisibleRevealDailyCombo] =
		useState<boolean>(false);
	const [visibleInfoDailyCombo, setVisibleInfoDailyCombo] =
		useState<boolean>(false);

	/*********************
	// #region Constants
	**********************/
	let cardsToRender: ComboCard[] = [];
	const placeholderCard: ComboCard = {
		id: '',
		image: process.env.VITE_DAILY_COMBO_CARD_HIDDEN_IMAGE!,
		title: '',
		description: '',
	};

	if (comboCards.length > 0) {
		cardsToRender = [...comboCards];
	}
	// Añadir placeholders si hay menos de 3 elementos
	while (cardsToRender.length < 3) {
		cardsToRender.push(placeholderCard);
	}

	// Asegurarse de que siempre tenemos exactamente 3 elementos
	const finalCardsToRender = cardsToRender.slice(0, 3);

	const handleCloseDialog = () => {
		setVisibleDrawerClaimCombo(false);
		onCloseClaim();
	};

	/*********************
	// #region Template
	**********************/
	return (
		<>
			<div className="flex justify-around items-end">
				<div className="mr-2">
					<img
						className="w-[110px]"
						src={process.env.VITE_DAILY_COMBO_CARD_IMAGE}
						alt="Daily Combo"
					/>
				</div>

				<div className="flex flex-col justify-end mb-[10px]">
					<div className="flex">
						{canClaimReward && !rewardCollected ? (
							<div className="flex gap-1 self-end justify-end items-center w-full mb-2">
								<Button
									variant={'claim'}
									className="text-sm rounded-lg w-full text-center inline-flex items-center"
									onClick={() =>
										setVisibleDrawerClaimCombo(true)
									}
								>
									<span className="pr-[3px] font-normal">
										{t('general.claim')}
									</span>
									<span>
										<span className="font-bold">
											{numify(levelCombo.coinsReward)}
										</span>

										<CoinImage classes="w-[16px] inline" />
										<span className="pr-1 font-normal">
											and
										</span>

										<span className="font-bold pr-[3px]">
											{numify(levelCombo.diamondsReward)}
										</span>
										<DiamondImage classes="w-[12px] inline" />
									</span>
								</Button>
							</div>
						) : (
							<div className="w-full">
								{!rewardCollected && (
									<div className="flex flex-col items-center text-sm font-bold">
										<p className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 via-customer-500 to-pink-500  text-black">
											{t(
												'dailycombo.reveal.winThisPrizes'
											)}
										</p>
										<div className="flex w-full justify-center gap-3 mb-2 items-center ">
											<div className="flex gap-1 self-end justify-end items-center">
												<CoinImage classes="w-[20px]" />

												{
													<span>
														{numify(
															levelCombo.coinsReward
														)}
													</span>
												}
											</div>

											<div className="flex gap-1 items-center">
												<DiamondImage classes="w-[18px]" />
												{
													<span className="leading-none">
														{numify(
															levelCombo.diamondsReward
														)}
													</span>
												}
											</div>
										</div>
									</div>
								)}
							</div>
						)}
					</div>

					<div className="flex items-center justify-between mb-2 gap-2">
						{!rewardCollected && !canClaimReward && (
							<div
								className="flex gap-2 px-2 py-1 rounded-full relative border-gradient-r-reveal-combo-gray-900 gradient-border-2 mt-[1px] mb-[-1px]"
								onClick={() => setVisibleRevealDailyCombo(true)}
							>
								<img
									src={process.env.VITE_MINI_DIAMOND_IMAGE}
									alt="Reveal daily combo"
									className="w-8 h-8 absolute top-[-5px] left-[-11px] drop-shadow-lg"
								/>
								<span className="text-[9px] pl-4">
									{t('dailycombo.reveal.title')}
								</span>
							</div>
						)}

						{rewardCollected && (
							<div className="flex gap-1 self-end justify-end items-center">
								<CoinImage classes="w-[20px]" />

								<p className="text-sm font-bold">
									{t('general.claimed')}
								</p>
							</div>
						)}

						<div>
							<Button
								variant={'secondary'}
								className="text-[9px] py-1 px-3 rounded-full"
								onClick={() => setVisibleInfoDailyCombo(true)}
							>
								{t('dailycombo.reveal.viewMore')}
							</Button>
						</div>
					</div>
					<div className="flex flex-col justify-end gap-2">
						<div className="flex gap-2 items-end">
							{finalCardsToRender.map((combo, index) => (
								<DailyComboCard key={index} card={combo} />
							))}
						</div>
					</div>
				</div>
			</div>

			<ClaimDailyCombo
				isOpen={visibleDrawerClaimCombo}
				onClose={handleCloseDialog}
			/>

			<RevealDailyCombo
				isOpen={visibleRevealDailyCombo}
				onClose={setVisibleRevealDailyCombo}
			/>

			<InfoDailyCombo
				isOpen={visibleInfoDailyCombo}
				onClose={setVisibleInfoDailyCombo}
			/>
		</>
	);
};
